import type { LearnerCountersRow } from '~/gql/billing/billing'
import { useQuery } from '@apollo/client/react'
import { BillingTier } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_LEARNER_COUNTERS_BY_ORGANIZATION } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'
import { formatCents } from '~/pages/backoffice/admin/billing/money'
import { currentMonth } from '~/pages/backoffice/admin/billing/month'
import MonthPicker from '~/pages/backoffice/admin/billing/MonthPicker'
import OrgStorageTable from '~/pages/backoffice/admin/billing/OrgStorageTable'

/**
 * Compteurs d'apprenants et palier par organisation (#45, #51, #183).
 *
 * Écran d'OBSERVATION : il ne pilote aucun blocage, et le palier s'y lit sans s'y
 * modifier — c'est l'admin de l'organisation qui le choisit. Le franchissement de
 * borne est signalé, jamais appliqué (BUSINESS_MODEL.md).
 */
function LearnerCountersTable() {
  const { t, i18n } = useTranslation()
  const [month, setMonth] = useState(() => currentMonth())

  const { data, loading } = useQuery(GQL_LEARNER_COUNTERS_BY_ORGANIZATION, {
    client,
    variables: { month },
    fetchPolicy: 'network-only',
  })

  const rows = (data?.learnerCountersByOrganization ?? []) as unknown as LearnerCountersRow[]
  const totalMonthly = rows.reduce((sum, row) => sum + row.monthlyActive, 0)
  const totalLearners = rows.reduce((sum, row) => sum + row.learnerCount, 0)
  const totalRegistered = rows.reduce((sum, row) => sum + row.registeredLearners, 0)
  const totalRevenue = rows.reduce((sum, row) => sum + row.estimatedRevenueCents, 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">{t('billing.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('billing.help')}</p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.organizations')}</TableHead>
              <TableHead className="text-right">{t('billing.learnerCount')}</TableHead>
              <TableHead className="text-right">{t('billing.monthlyActive')}</TableHead>
              <TableHead className="text-right">{t('billing.registered')}</TableHead>
              <TableHead>{t('billing.plan')}</TableHead>
              <TableHead className="text-right">{t('billing.estimatedRevenue')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.orgId}>
                <TableCell className="font-medium">
                  {row.orgName}
                  <span className="ml-2 text-muted-foreground">{row.orgSlug}</span>
                </TableCell>
                {/* Métrique de facturation : apprenants distincts sur 12 mois glissants,
                    avec les places restant avant le palier suivant. */}
                <TableCell className="text-right tabular-nums">
                  {row.learnerCount}
                  {row.placesRemaining != null && row.nextTier && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t('billing.placesShort', {
                        places: row.placesRemaining,
                        next: t(`billing.tier.${row.nextTier}`),
                      })}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.monthlyActive}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.registeredLearners}
                </TableCell>
                <TableCell>
                  {t(`billing.tier.${row.billedTier}`)}
                  {row.billedTier !== BillingTier.FREE && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t(`billing.term.${row.paymentTerm}`)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCents(row.estimatedRevenueCents, i18n.language)}
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow className="font-medium">
                <TableCell>{t('billing.total')}</TableCell>
                <TableCell className="text-right tabular-nums">{totalLearners}</TableCell>
                <TableCell className="text-right tabular-nums">{totalMonthly}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {totalRegistered}
                </TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">
                  {formatCents(totalRevenue, i18n.language)}
                </TableCell>
              </TableRow>
            )}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {t('billing.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Second axe d'observation : les apprenants disent ce que la plateforme
          facture, le stockage dit ce qu'elle coûte (#180). */}
      <OrgStorageTable />
    </div>
  )
}

export default LearnerCountersTable
